import type { Tenant } from '../lib/database.types';
import { hoursArrayToMap, displayHours } from '../lib/slots';
import Socials from './Socials';

/**
 * Footer de la vitrine — porté À L'IDENTIQUE du legacy (Food Kourt / Big Burger / TFK).
 * Fond sombre, 4 colonnes : marque + pitch + réseaux · Contact · Horaires · Liens utiles,
 * puis la barre « © année — propulsé par Lil & Co Click&Collect ». Tout piloté par le tenant.
 */
interface StoreFooterProps {
  tenant: Tenant;
  onHome: () => void;
  onMenu: () => void;
}

export default function StoreFooter({ tenant, onHome, onMenu }: StoreFooterProps) {
  const rows = displayHours(hoursArrayToMap(tenant.opening_hours ?? []));
  const hasHours = rows.some(([, h]) => h !== 'Fermé');
  const pitch = tenant.short_pitch?.trim() || null;
  const addressParts = [
    tenant.address_line,
    [tenant.postal_code, tenant.city].filter(Boolean).join(' '),
  ].filter((p) => p && p.trim().length > 0);
  const year = new Date().getFullYear();

  return (
    <footer className="mt-16 bg-gray-900 text-gray-300">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:grid-cols-2 lg:grid-cols-4 lg:px-6">
        {/* Marque + pitch + réseaux */}
        <div>
          <div className="flex items-center gap-3">
            {tenant.logo_url ? (
              <img
                src={tenant.logo_url}
                alt={tenant.name}
                className="h-11 w-11 rounded-full object-contain"
              />
            ) : (
              <span
                className="flex h-11 w-11 items-center justify-center rounded-full text-base font-black text-white"
                style={{ background: 'var(--brand-primary)' }}
              >
                {tenant.name.slice(0, 1)}
              </span>
            )}
            <span className="flex flex-col leading-none">
              <span className="font-bold text-white">{tenant.name}</span>
              {tenant.city && (
                <span className="mt-0.5 text-xs font-semibold uppercase tracking-[0.14em] text-gray-400">
                  {tenant.city}
                </span>
              )}
            </span>
          </div>
          {pitch && <p className="mt-4 text-sm leading-relaxed">{pitch}</p>}
          <div className="mt-4">
            <div className="mb-2 text-xs font-bold uppercase tracking-wide opacity-70">
              Suivez-nous
            </div>
            <Socials socials={tenant.socials} />
          </div>
        </div>

        {/* Contact */}
        <div>
          <h4 className="mb-4 text-sm font-extrabold uppercase tracking-wide text-white">Contact</h4>
          <div className="space-y-2 text-sm">
            {addressParts.length > 0 && <div>📍 {addressParts.join(', ')}</div>}
            {tenant.phone1 && (
              <div>
                📞{' '}
                <a href={`tel:${tenant.phone1}`} className="hover:text-white">
                  {tenant.phone1}
                </a>
              </div>
            )}
          </div>
        </div>

        {/* Horaires */}
        {hasHours && (
          <div>
            <h4 className="mb-4 text-sm font-extrabold uppercase tracking-wide text-white">
              Horaires
            </h4>
            <div className="space-y-1.5 text-sm">
              {rows.map(([day, h]) => (
                <div key={day} className="flex justify-between gap-2">
                  <span>{day.slice(0, 3)}.</span>
                  <span className="text-right text-xs text-gray-400">{h}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Liens utiles */}
        <div>
          <h4 className="mb-4 text-sm font-extrabold uppercase tracking-wide text-white">
            Liens utiles
          </h4>
          <div className="space-y-2 text-sm">
            <button onClick={onHome} className="block hover:text-white">
              Accueil
            </button>
            <button onClick={onMenu} className="block hover:text-white">
              Menu
            </button>
            <a href="#" className="block hover:text-white">
              Mentions légales
            </a>
            <a href="#" className="block hover:text-white">
              CGV
            </a>
            <a href="#" className="block hover:text-white">
              Politique de confidentialité
            </a>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-800">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 py-5 text-xs text-gray-500 sm:flex-row lg:px-6">
          <div>
            © {year} {tenant.name}
          </div>
          <div className="flex items-center gap-2">
            <span>Site propulsé par</span>
            <span className="font-bold text-gray-300">Lil &amp; Co Click&amp;Collect</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
